import { transform } from "../../../utils/transform";
import { ScaleLinear, ScaleBand } from "d3-scale";
import { Selection, BaseType, select } from "d3-selection";
import {
    BarDataItem,
    BarChartGroupItem,
    BarChartTooltipFromatterInputData,
} from "./bar-chart";
import { Transition } from "d3-transition";

export class BarChartSvgBars {
    selection: Selection<
        SVGGElement,
        BarDataItem,
        SVGGElement,
        BarChartGroupItem
    >;
    constructor(selection) {
        this.selection = selection;
    }

    public addTooltipHandler(
        tooltipElement: HTMLUicTooltipElement,
        xScale: ScaleBand<string>,
        yScale: ScaleLinear<number, number>,
        xOffset: number,
        yOffset: number,
        tooltipTextFormatter: (x: BarChartTooltipFromatterInputData) => string
    ) {
        if (!tooltipElement) {
            this.selection.on("touchmove mousemove touchstart", null);
            this.selection.on("touchend mouseleave", null);
            return this;
        }

        select(tooltipElement).on("touchend mouseleave", function () {
            tooltipElement.open = false;
        });

        this.selection.on("touchmove mousemove touchstart", function (
            event: Event,
            d: BarDataItem & { data: { groupKey: string } }
        ) {
            event.preventDefault();

            // reopen to trigger popperjs placement
            tooltipElement.open = false;
            tooltipElement.open = true;

            const { groupKey, ...values } = d.data;
            const x = xScale(groupKey) + xOffset;
            const y = Object.values(values).reduce((s, c) => s + c) / 2;
            const selectedValue = d[1] - d[0];

            select(tooltipElement)
                .style("position", "absolute")
                .style("display", "block")
                .style("left", x + "px")
                .style("top", yScale(y) + yOffset + "px")
                .select('[slot="tip"]')
                .html(
                    transform(tooltipTextFormatter, {
                        key: d.data.groupKey,
                        value: selectedValue,
                        values: values,
                        hoveredGroupKey: d.dataGroupKey,
                    })
                );
        });

        this.selection.on("touchend mouseleave", function (event: MouseEvent) {
            if (
                event.relatedTarget &&
                (event.relatedTarget as HTMLElement).closest("uic-tooltip")
            ) {
                return;
            }

            tooltipElement.open = false;
        });

        return this;
    }

    public updateInBarLabels(
        xScale: ScaleBand<string>,
        yScale: ScaleLinear<number, number>,
        yFormatter: (x: number) => string,
        transitionDurationMs: number
    ) {
        const inBarLabels = this.selection
            .selectAll<SVGTextElement, BarDataItem>(
                ".uic-bar-chart__in-bar-label"
            )
            .data((d) => [d]);

        const enteringBarLabels = inBarLabels
            .enter()
            .append("text")
            .attr("class", "uic-bar-chart__in-bar-label")
            .attr("fill", "black")
            .attr("dominant-baseline", "middle")
            .attr("text-anchor", "middle")
            .attr("filter", "url(#uic-bar-chart__in-bar-label-background)")
            .attr("style", "opacity: 0")
            .call((s) =>
                BarChartSvgBars.positionInBarLabels(s, xScale, yScale)
            );

        inBarLabels
            .transition()
            .duration(transitionDurationMs)
            .call((s) =>
                BarChartSvgBars.positionInBarLabels(s, xScale, yScale)
            );

        inBarLabels.exit().remove();

        const existingInBarLabels = enteringBarLabels
            .merge(inBarLabels)
            .text((d) => transform(yFormatter, d.data[d.dataGroupKey]));
        const maxBarLength = existingInBarLabels
            .nodes()
            .map((n) => n.getComputedTextLength())
            .reduce((max, x) => (x > max ? x : max));

        existingInBarLabels
            .transition()
            .delay(transitionDurationMs)
            .duration(transitionDurationMs / 5)
            .attr(
                "style",
                (d) =>
                    `opacity: ${
                        maxBarLength < xScale.bandwidth() * 0.8 &&
                        yScale(d[0]) - yScale(d[1]) > 25
                            ? "1"
                            : "0"
                    }`
            );
    }

    private static positionInBarLabels(
        selection:
            | Selection<SVGTextElement, BarDataItem, BaseType, unknown>
            | Transition<SVGTextElement, BarDataItem, BaseType, unknown>,
        xScale: ScaleBand<string>,
        yScale: ScaleLinear<number, number>
    ) {
        selection
            .attr(
                "x",
                (d) =>
                    (xScale(d.data.groupKey.toString()) || 0) +
                    xScale.bandwidth() / 2
            )
            .attr("y", function (d) {
                return yScale(d[1] + (d[0] - d[1]) / 2);
            });
    }
}
